/**
 * ElementRenderer.js
 *
 * Dibuja un elemento. El MISMO componente sirve para el lienzo del editor y para
 * la página pública en React — solo cambia el flag `editable`. Es lo que garantiza
 * que "lo que ves es lo que se publica"; dos renderers distintos siempre terminan
 * divergiendo en algún detalle y erosionando la confianza en el editor.
 *
 * La edición inline usa `contentEditable` sin controlar el valor desde React
 * (`suppressContentEditableWarning` + commit en `blur`). Controlarlo desde el
 * estado en cada tecla rompe la posición del cursor en cuanto el texto tiene
 * acentos o el usuario edita por el medio — un clásico.
 */

import React, { useRef, useEffect, useCallback } from 'react';
import { effectiveStyle, toReactStyle } from './builderModel';
import API_URL from '../../../config/api';

/* ------------------------------------------------------------------ *
 * Iconos (mismos paths que el renderer del servidor)
 * ------------------------------------------------------------------ */

export const ICON_PATHS = {
  star: 'M12 2l3 6.6 7.2.8-5.4 4.9 1.5 7.1L12 17.8 5.7 21.4l1.5-7.1L1.8 9.4 9 8.6z',
  clock: 'M12 2a10 10 0 100 20 10 10 0 000-20zm1 10.6V6h-2v7.4l5.1 3 1-1.7z',
  heart: 'M12 21s-8-5.1-8-10.2A4.8 4.8 0 0112 7a4.8 4.8 0 018 3.8C20 15.9 12 21 12 21z',
  shield: 'M12 2l8 3.5v6c0 5-3.4 9.4-8 10.5-4.6-1.1-8-5.5-8-10.5v-6z',
  chart: 'M4 20h16v2H2V2h2zm3-3V9h3v8zm5 0V5h3v12zm5 0v-6h3v6z',
  users: 'M16 11a4 4 0 10-4-4 4 4 0 004 4zm-8 1a3 3 0 10-3-3 3 3 0 003 3zm8 2c-3 0-8 1.5-8 4.5V21h16v-2.5c0-3-5-4.5-8-4.5z',
  gift: 'M20 8h-2.2A3 3 0 0012 4.5 3 3 0 006.2 8H4v4h16zM4 14v6h7v-6zm9 0v6h7v-6z',
  check: 'M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z',
  phone: 'M6.6 10.8a15 15 0 006.6 6.6l2.2-2.2a1 1 0 011-.2 11 11 0 003.5.6 1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1 11 11 0 00.6 3.5 1 1 0 01-.3 1z',
  mail: 'M20 4H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V6a2 2 0 00-2-2zm0 4l-8 5-8-5V6l8 5 8-5z',
  whatsapp: 'M20 12a8 8 0 01-11.9 7L4 20l1.1-4A8 8 0 1120 12z',
  instagram: 'M12 7.5A4.5 4.5 0 1016.5 12 4.5 4.5 0 0012 7.5zm0 7.4A2.9 2.9 0 1114.9 12 2.9 2.9 0 0112 14.9zM17 3H7a4 4 0 00-4 4v10a4 4 0 004 4h10a4 4 0 004-4V7a4 4 0 00-4-4z',
  facebook: 'M13 22v-8h3l.5-4H13V7.5c0-1.1.3-1.9 2-1.9h2V2.1A28 28 0 0014.2 2C11.4 2 9.5 3.7 9.5 6.9V10H6v4h3.5v8z',
  tiktok: 'M16 2h3a5 5 0 004 4v3a8 8 0 01-4-1.2V15a6 6 0 11-6-6v3a3 3 0 103 3z'
};

export function Icon({ name, size, color }) {
  return (
    <svg width={size || 24} height={size || 24} viewBox="0 0 24 24"
      fill={color || 'currentColor'} aria-hidden="true" style={{ display: 'inline-block' }}>
      <path d={ICON_PATHS[name] || ICON_PATHS.star} />
    </svg>
  );
}

/* ------------------------------------------------------------------ *
 * Edición inline
 * ------------------------------------------------------------------ */

function Editable({ tag, html, plain, onCommit, editable, className, style, placeholder }) {
  const ref = useRef(null);
  const Tag = tag || 'div';

  // Se sincroniza desde fuera SOLO si el nodo no tiene el foco. Así el undo/redo
  // y el panel lateral pueden cambiar el texto sin pelearse con quien escribe.
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (document.activeElement === node) return;
    const next = plain ? (html || '') : (html || '');
    if (plain) {
      if (node.textContent !== next) node.textContent = next;
    } else if (node.innerHTML !== next) {
      node.innerHTML = next;
    }
  }, [html, plain]);

  const commit = useCallback(() => {
    const node = ref.current;
    if (!node) return;
    onCommit(plain ? node.textContent : node.innerHTML);
  }, [onCommit, plain]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); e.currentTarget.blur(); }
    // En títulos y botones, Enter cierra la edición en vez de meter un salto de
    // línea que rompe el diseño.
    if (e.key === 'Enter' && plain) { e.preventDefault(); e.currentTarget.blur(); }
    e.stopPropagation(); // que Supr/Ctrl+Z no borren el bloque mientras se escribe
  };

  if (!editable) {
    return plain
      ? <Tag className={className} style={style}>{html}</Tag>
      : <Tag className={className} style={style} dangerouslySetInnerHTML={{ __html: html || '' }} />;
  }

  return (
    <Tag
      ref={ref}
      className={(className || '') + ' bld-editable'}
      style={style}
      contentEditable
      suppressContentEditableWarning
      data-placeholder={placeholder || 'Escribe aquí…'}
      onBlur={commit}
      onKeyDown={onKeyDown}
      onClick={(e) => e.stopPropagation()}
      spellCheck={false}
    />
  );
}

/* ------------------------------------------------------------------ *
 * Utilidades
 * ------------------------------------------------------------------ */

export function buttonHref(p) {
  switch (p.action) {
    case 'anchor': return '#' + (p.anchor || '');
    case 'phone': return 'tel:' + (p.phone || '');
    case 'email': return 'mailto:' + (p.email || '');
    case 'whatsapp':
      return 'https://wa.me/' + String(p.phone || '').replace(/[^0-9]/g, '') +
        (p.message ? '?text=' + encodeURIComponent(p.message) : '');
    default: return p.url || '#';
  }
}

export function videoEmbed(src) {
  const yt = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{6,})/.exec(src || '');
  if (yt) return 'https://www.youtube.com/embed/' + yt[1];
  const vm = /vimeo\.com\/(\d+)/.exec(src || '');
  if (vm) return 'https://player.vimeo.com/video/' + vm[1];
  return null;
}

function Countdown({ target, expiredText, style }) {
  const [left, setLeft] = React.useState(null);
  useEffect(() => {
    const end = new Date(target).getTime();
    if (!target || Number.isNaN(end)) { setLeft(null); return undefined; }
    const tick = () => {
      const diff = end - Date.now();
      if (diff <= 0) { setLeft('expired'); return; }
      const s = Math.floor(diff / 1000);
      setLeft({ d: Math.floor(s / 86400), h: Math.floor(s / 3600) % 24, m: Math.floor(s / 60) % 60, s: s % 60 });
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [target]);

  if (left === null) return <div style={style} className="bld-ph">Configura la fecha objetivo</div>;
  if (left === 'expired') return <div style={style}>{expiredText || 'Terminó'}</div>;
  const units = [['d', 'Días'], ['h', 'Horas'], ['m', 'Min'], ['s', 'Seg']];
  return (
    <div className="pb-countdown" style={style}>
      {units.map(([k, label]) => (
        <div className="u" key={k}>
          <b>{String(left[k]).padStart(2, '0')}</b><span>{label}</span>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Formulario
 * ------------------------------------------------------------------ */

function FormElement({ node, editable, onProps, siteSlug }) {
  const p = node.props || {};
  const [state, setState] = React.useState({ sending: false, msg: '', ok: false });

  const submit = async (e) => {
    e.preventDefault();
    if (editable) return;
    const fd = new FormData(e.target);
    const data = { _element: node.id };
    fd.forEach((v, k) => { data[k] = v; });
    setState({ sending: true, msg: 'Enviando…', ok: false });
    try {
      const r = await fetch(API_URL + '/api/public/site/' + siteSlug + '/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      const j = await r.json();
      if (!r.ok) { setState({ sending: false, msg: j.error || 'No se pudo enviar', ok: false }); return; }
      if (j.redirectUrl) { window.location.href = j.redirectUrl; return; }
      setState({ sending: false, msg: j.message || p.successMessage || '¡Gracias!', ok: true });
      e.target.reset();
    } catch (err) {
      setState({ sending: false, msg: 'Error de conexión', ok: false });
    }
  };

  return (
    <form className="pb-form" onSubmit={submit}>
      {p.title !== undefined && (
        <Editable tag="h3" plain html={p.title} editable={editable}
          onCommit={(v) => onProps({ title: v })} placeholder="Título del formulario" />
      )}
      <div className="pb-hp"><input type="text" name="_hp" tabIndex={-1} autoComplete="off" /></div>
      {(p.fields || []).map((f) => (
        <div className="fld" key={f.key}>
          {f.type !== 'checkbox' && (
            <label>{f.label}{f.required ? <i> *</i> : null}</label>
          )}
          {f.type === 'textarea' && <textarea name={f.key} rows={4} placeholder={f.placeholder} required={f.required} />}
          {f.type === 'select' && (
            <select name={f.key} required={f.required} defaultValue="">
              <option value="">Selecciona…</option>
              {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          )}
          {f.type === 'checkbox' && (
            <label className="chk"><input type="checkbox" name={f.key} required={f.required} /> <span>{f.label}</span></label>
          )}
          {!['textarea', 'select', 'checkbox'].includes(f.type) && (
            <input type={f.type || 'text'} name={f.key} placeholder={f.placeholder} required={f.required} />
          )}
        </div>
      ))}
      <button type="submit" className="pb-btn" disabled={state.sending}>
        {p.submitLabel || 'Enviar'}
      </button>
      {state.msg ? <p className={'fmsg ' + (state.ok ? 'ok' : 'err')}>{state.msg}</p> : null}
      {editable ? <p className="bld-note">Los campos se configuran en el panel de la derecha.</p> : null}
    </form>
  );
}

/* ------------------------------------------------------------------ *
 * Renderer
 * ------------------------------------------------------------------ */

function ElementRendererInner({ node, breakpoint, editable, onProps, siteSlug, bookingSlug }) {
  const p = node.props || {};
  const style = toReactStyle(effectiveStyle(node, breakpoint));
  const set = (patch) => onProps && onProps(node.id, patch);

  switch (node.type) {
    case 'heading':
      return (
        <Editable tag={/^h[1-6]$/.test(p.tag) ? p.tag : 'h2'} plain html={p.text} style={style}
          editable={editable} onCommit={(v) => set({ text: v })} placeholder="Tu título" />
      );

    case 'text':
      return (
        <Editable tag="div" html={p.html} style={style} className="pb-rich"
          editable={editable} onCommit={(v) => set({ html: v })} placeholder="Tu texto" />
      );

    case 'button': {
      const cls = 'pb-btn pb-btn-' + (p.variant || 'solid');
      if (editable) {
        return (
          <div className="pb-btn-wrap">
            <Editable tag="span" plain html={p.label} className={cls} style={style}
              editable onCommit={(v) => set({ label: v })} placeholder="Texto del botón" />
          </div>
        );
      }
      return (
        <div className="pb-btn-wrap">
          <a className={cls} style={style} href={buttonHref(p)}
            target={p.newTab ? '_blank' : undefined} rel={p.newTab ? 'noopener noreferrer' : undefined}>
            {p.label}
          </a>
        </div>
      );
    }

    case 'image': {
      if (!p.src) return <div className="bld-ph" style={style}>Haz clic y elige una imagen →</div>;
      const img = <img src={p.src} alt={p.alt || ''} style={style} loading="lazy" />;
      if (p.link && !editable) {
        return <a href={p.link} target={p.newTab ? '_blank' : undefined} rel="noopener noreferrer">{img}</a>;
      }
      return img;
    }

    case 'video': {
      const embed = videoEmbed(p.src);
      if (!p.src) return <div className="bld-ph" style={style}>Pega el enlace de YouTube o Vimeo →</div>;
      if (embed) {
        return (
          <div className="pb-ratio" style={style}>
            <iframe src={embed} title="video" frameBorder="0" allowFullScreen loading="lazy" />
          </div>
        );
      }
      return <video src={p.src} controls style={Object.assign({ width: '100%' }, style)} />;
    }

    case 'icon':
      return <div style={style}><Icon name={p.name} size={p.size || 32} /></div>;

    case 'divider':
      return <hr style={Object.assign({ border: 0, borderTop: (p.thickness || 1) + 'px solid currentColor', opacity: 0.25 }, style)} />;

    case 'spacer':
      return <div style={Object.assign({}, style, { height: (p.height || 40) + 'px' })} />;

    case 'list':
      return (
        <ul className="pb-list" style={style}>
          {(p.items || []).map((item, i) => (
            <li key={i}>
              {p.icon ? <Icon name={p.icon} size={18} color="var(--c-primary)" /> : null}
              <Editable tag="span" plain html={item} editable={editable}
                onCommit={(v) => {
                  const items = (p.items || []).slice();
                  if (!v.trim()) items.splice(i, 1); else items[i] = v;
                  set({ items });
                }} />
            </li>
          ))}
          {editable && (
            <li className="bld-add">
              <button type="button" onClick={(e) => { e.stopPropagation(); set({ items: (p.items || []).concat(['Nuevo ítem']) }); }}>
                + Agregar ítem
              </button>
            </li>
          )}
        </ul>
      );

    case 'form':
      return <div style={style}><FormElement node={node} editable={editable} siteSlug={siteSlug} onProps={(patch) => set(patch)} /></div>;

    case 'booking': {
      const slug = p.bookingSlug || bookingSlug || '';
      if (!slug) return <div className="bld-ph" style={style}>Elige un calendario en el panel derecho →</div>;
      if (editable) return <div className="bld-ph" style={style}>📅 Calendario «{slug}» — se muestra al publicar</div>;
      return <iframe title="Agendar" src={'/reservar/' + slug + '?embed=1'}
        style={Object.assign({ width: '100%', border: 0, height: (p.height || 720) + 'px' }, style)} />;
    }

    case 'map':
      if (!p.query) return <div className="bld-ph" style={style}>Escribe la dirección en el panel derecho →</div>;
      return <iframe title="mapa" loading="lazy"
        src={'https://www.google.com/maps?q=' + encodeURIComponent(p.query) + '&output=embed'}
        style={Object.assign({ width: '100%', border: 0, height: (p.height || 320) + 'px' }, style)} />;

    case 'social':
      return (
        <div className="pb-social" style={style}>
          {(p.links || []).filter((l) => l.network).map((l, i) => (
            editable
              ? <span key={i} title={l.url || 'sin enlace'}><Icon name={l.network} size={22} /></span>
              : <a key={i} href={l.url || '#'} target="_blank" rel="noopener noreferrer" aria-label={l.network}>
                  <Icon name={l.network} size={22} />
                </a>
          ))}
        </div>
      );

    case 'html':
      return <div style={style} dangerouslySetInnerHTML={{ __html: p.code || '' }} />;

    case 'countdown':
      return <Countdown target={p.target} expiredText={p.expiredText} style={style} />;

    case 'testimonial':
      return (
        <figure className="pb-tst" style={style}>
          <div className="stars">{'★'.repeat(Math.max(0, Math.min(5, p.stars ?? 5)))}</div>
          <Editable tag="blockquote" plain html={p.quote} editable={editable}
            onCommit={(v) => set({ quote: v })} placeholder="El testimonio" />
          <figcaption>
            {p.avatar ? <img src={p.avatar} alt="" /> : null}
            <span>
              <Editable tag="strong" plain html={p.author} editable={editable}
                onCommit={(v) => set({ author: v })} placeholder="Nombre" />
              <Editable tag="em" plain html={p.role} editable={editable}
                onCommit={(v) => set({ role: v })} placeholder="Cargo" />
            </span>
          </figcaption>
        </figure>
      );

    case 'pricing':
      return (
        <div className={'pb-price' + (p.highlighted ? ' hi' : '')} style={style}>
          <Editable tag="h4" plain html={p.plan} editable={editable} onCommit={(v) => set({ plan: v })} placeholder="Nombre del plan" />
          <div className="amount">
            <Editable tag="strong" plain html={p.price} editable={editable} onCommit={(v) => set({ price: v })} placeholder="$0" />
            <Editable tag="span" plain html={p.period} editable={editable} onCommit={(v) => set({ period: v })} placeholder="/mes" />
          </div>
          <ul className="pb-list">
            {(p.features || []).map((f, i) => (
              <li key={i}>
                <Icon name="check" size={16} color="var(--c-primary)" />
                <Editable tag="span" plain html={f} editable={editable}
                  onCommit={(v) => {
                    const features = (p.features || []).slice();
                    if (!v.trim()) features.splice(i, 1); else features[i] = v;
                    set({ features });
                  }} />
              </li>
            ))}
            {editable && (
              <li className="bld-add">
                <button type="button" onClick={(e) => { e.stopPropagation(); set({ features: (p.features || []).concat(['Nueva característica']) }); }}>
                  + Característica
                </button>
              </li>
            )}
          </ul>
          {p.ctaLabel ? (
            editable
              ? <Editable tag="span" plain html={p.ctaLabel} className="pb-btn pb-btn-solid full"
                  editable onCommit={(v) => set({ ctaLabel: v })} />
              : <a className="pb-btn pb-btn-solid full" href={p.ctaUrl || '#'}>{p.ctaLabel}</a>
          ) : null}
        </div>
      );

    case 'faq':
      return (
        <div className="pb-faq" style={style}>
          {(p.items || []).map((it, i) => (
            <details key={i} open={editable}>
              <summary>
                <Editable tag="span" plain html={it.q} editable={editable}
                  onCommit={(v) => {
                    const items = (p.items || []).slice();
                    items[i] = Object.assign({}, items[i], { q: v });
                    set({ items });
                  }} placeholder="La pregunta" />
              </summary>
              <Editable tag="p" plain html={it.a} editable={editable}
                onCommit={(v) => {
                  const items = (p.items || []).slice();
                  items[i] = Object.assign({}, items[i], { a: v });
                  set({ items });
                }} placeholder="La respuesta" />
            </details>
          ))}
          {editable && (
            <button type="button" className="bld-add-btn"
              onClick={(e) => { e.stopPropagation(); set({ items: (p.items || []).concat([{ q: 'Nueva pregunta', a: 'Respuesta' }]) }); }}>
              + Agregar pregunta
            </button>
          )}
        </div>
      );

    default:
      return <div className="bld-ph">Bloque desconocido: {node.type}</div>;
  }
}

// El lienzo se re-renderiza entero en cada cambio; memoizar por nodo evita que
// escribir en un bloque redibuje los otros doscientos.
const ElementRenderer = React.memo(ElementRendererInner, (a, b) =>
  a.node === b.node && a.breakpoint === b.breakpoint && a.editable === b.editable &&
  a.siteSlug === b.siteSlug && a.bookingSlug === b.bookingSlug);

export default ElementRenderer;
export { Editable };
